import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Tag } from 'antd';
import { Select } from 'ALIASED-antd';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';
import { Case } from 'vl-common/src/schemas/shared/case';
import LetterDocuments from '@components/Documents/LetterDocuments';

const CaseSelectWrapper = styled.div`
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CaseOptionLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const CaseOptionCondition = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

type OtherCaseDocumentsProps = {
  patientUserGuid: string;
  currentCaseId: number;
};

const OtherCaseDocuments = ({ patientUserGuid, currentCaseId }: OtherCaseDocumentsProps) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [selectedCaseId, setSelectedCaseId] = useState<string>();

  const { data: casesData, isLoading: isLoadingCasesData } = useQuery({
    queryKey: ['patientCases', patientUserGuid],
    queryFn: () =>
      Api.cases.searchForCases({
        rows: 999,
        patient: patientUserGuid,
        all_specs: true
      }),
    enabled: !!patientUserGuid
  });

  const casesList: Case[] = (casesData?.data?.cases || []).filter(
    (singleCase: Case) => singleCase.case_id !== currentCaseId
  );

  const otherCaseOptions = useMemo(
    () =>
      casesList.map((singleCase: Case) => ({
        label: (
          <CaseOptionLabel>
            <CaseOptionCondition>{singleCase.cases_dropdown}</CaseOptionCondition>
            <Tag color={singleCase.case_status === 'DISCHARGE' ? 'default' : 'green'} style={{ flexShrink: 0 }}>
              {singleCase.case_status_desc}
            </Tag>
          </CaseOptionLabel>
        ),
        value: `${singleCase.case_id}`
      })),
    [casesData]
  );

  return (
    <div data-testid="documents-other-cases">
      <Row style={{ margin: '0px 8px 20px 20px' }}>
        <Col span={24}>
          <label htmlFor="documents-other-case-select" style={{ display: 'block', marginBottom: 8 }}>
            {t('Select case:')}
          </label>
          <CaseSelectWrapper>
            <Select
              id="documents-other-case-select"
              options={otherCaseOptions}
              onChange={setSelectedCaseId}
              value={selectedCaseId}
              loading={isLoadingCasesData}
              style={{ width: '100%' }}
              placeholder={t('Select a case')}
              data-testid="documents-other-case-select"
            />
          </CaseSelectWrapper>
        </Col>
      </Row>
      {selectedCaseId && <LetterDocuments caseId={Number(selectedCaseId)} data-testid="other-case-documents-table" />}
    </div>
  );
};

export default OtherCaseDocuments;
